import { useAuth } from "./auth";

const BASE = (import.meta as ImportMeta & { env: { VITE_API_URL?: string } }).env
  .VITE_API_URL ?? "/api";

interface RequestOptions extends Omit<RequestInit, "body"> {
  body?: unknown;
}

export async function api<T>(path: string, opts: RequestOptions = {}): Promise<T> {
  const token = useAuth.getState().token;
  const headers: Record<string, string> = {
    "Content-Type": "application/json",
    ...((opts.headers as Record<string, string>) ?? {}),
  };
  if (token) headers["Authorization"] = `Bearer ${token}`;

  const res = await fetch(`${BASE}${path}`, {
    ...opts,
    headers,
    body: opts.body !== undefined ? JSON.stringify(opts.body) : undefined,
  });

  // 401 en login = credenciales inválidas, no sesión expirada
  if (res.status === 401 && !path.includes("/auth/login")) {
    useAuth.getState().logout();
    throw new Error("Sesión expirada");
  }

  if (!res.ok) {
    const text = await res.text();
    let msg = text || `HTTP ${res.status}`;
    try {
      const json = JSON.parse(text) as { message?: string | string[] };
      if (json.message) {
        msg = Array.isArray(json.message) ? json.message.join("; ") : json.message;
      }
    } catch {
      // texto plano
    }
    throw new Error(msg);
  }

  return (await res.json()) as T;
}

/** Descarga un archivo autenticado con el JWT actual y dispara save-as. */
export async function apiDownload(path: string, filename: string): Promise<void> {
  const token = useAuth.getState().token;
  const res = await fetch(`${BASE}${path}`, {
    headers: token ? { Authorization: `Bearer ${token}` } : {},
  });
  if (!res.ok) {
    throw new Error(`Error al descargar (${res.status})`);
  }
  const blob = await res.blob();
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// =============================================================
// Sucursales (compartido)
// =============================================================

export interface Sucursal {
  id: number;
  codigo: string;
  nombre: string;
}

export const sucursalesApi = {
  // Reusa el endpoint que ya existe (lee crm_sucursales activas de la org)
  list: () => api<Sucursal[]>(`/v1/reservas-admin/sucursales`),
};

// =============================================================
// Sprint 1.6 — wrappers de API para reservas (formulario público + admin)
// =============================================================

export const reservasApi = {
  createPublic: (dto: unknown) =>
    api<{ id: number; estado: string }>(`/v1/reservas/public`, {
      method: "POST",
      body: dto,
    }),

  list: (params: {
    estado?: string;
    desde?: string;
    hasta?: string;
    search?: string;
    page?: number;
    limit?: number;
  }) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => {
      if (v !== undefined && v !== "") q.set(k, String(v));
    });
    return api<{ items: unknown[]; total: number; page: number; limit: number }>(
      `/v1/reservas?${q.toString()}`,
    );
  },

  countPendientes: () =>
    api<{ count: number }>(`/v1/reservas/count-pendientes`),

  porVencer: () =>
    api<{ count: number; reservas: any[] }>(`/v1/reservas/por-vencer`),

  revertirAPendiente: (id: number, motivo?: string) =>
    api(`/v1/reservas/${id}/revertir-pendiente`, { method: "POST", body: { motivo } }),

  reabrir: (id: number, dto: { fechaReserva?: string; horaReserva?: string; numPersonas?: number; confirmarInmediatamente?: boolean }) =>
    api(`/v1/reservas/${id}/reabrir`, { method: 'POST', body: dto }),

  detail: (id: number) =>
    api<{ reserva: unknown; eventos: unknown[] }>(`/v1/reservas/${id}`),

  confirmar: (id: number, notas?: string) =>
    api(`/v1/reservas/${id}/confirmar`, { method: "POST", body: { notas } }),

  cancelar: (id: number, motivo?: string) =>
    api(`/v1/reservas/${id}/cancelar`, { method: "POST", body: { motivo } }),

  noShow: (id: number) =>
    api(`/v1/reservas/${id}/no-show`, { method: "POST" }),

  atendida: (id: number) =>
    api(`/v1/reservas/${id}/atendida`, { method: "POST" }),

  update: (id: number, dto: unknown) =>
    api(`/v1/reservas/${id}`, { method: "PATCH", body: dto }),
  sucursalMesas: (sucursalId: number) =>
    api<Array<{ sala: string; mesas: Array<{ id: number; numMesa: number; capacidad: number; ocupada: boolean }> }>>(
      `/v1/reservas/sucursal-mesas/${sucursalId}`,
    ),
  sucursalMesoneros: (sucursalId: number) =>
    api<Array<{ id: number; codVendedor: number; nombre: string }>>(
      `/v1/reservas/sucursal-mesoneros/${sucursalId}`,
    ),
  checkin: (id: number, mesaId: number, mesoneroId?: number) =>
    api(`/v1/reservas/${id}/checkin`, {
      method: "POST",
      body: mesoneroId ? { mesaId, mesoneroId } : { mesaId },
    }),
  reservarInfo: () =>
    api<{
      organizacionId: number;
      nombre: string;
      rubro: string | null;
      urlPostReserva: string | null;
      zonas: Array<{ id: number; nombre: string }>;
      sucursales: Array<{ id: number; codigo: string; nombre: string }>;
    }>(`/v1/reservas/reservar-info`),
};

export const reservasAdminApi = {
  sucursales: () =>
    api<Array<{ id: number; codigo: string; nombre: string }>>(
      `/v1/reservas-admin/sucursales`,
    ),

  buscarCliente: (q: string) =>
    api<Array<{
      codCliente: number; nombreCliente: string;
      email: string | null; telefono1: string | null;
      mobil: string | null; cif: string | null;
    }>>(`/v1/reservas-admin/buscar-cliente?q=${encodeURIComponent(q)}`),

  crearCliente: (dto: {
    nombreCliente: string; email?: string; telefono1?: string;
    mobil?: string; cif?: string; direccion1?: string; poblacion?: string;
  }) =>
    api<{
      creado: boolean; yaExistia: boolean;
      codCliente: number; nombreCliente: string; mensaje?: string;
    }>(`/v1/reservas-admin/cliente`, { method: "POST", body: dto }),

  crearReserva: (dto: {
    codCliente: number; sucursalId: number; nombreCliente: string;
    telefono?: string; email?: string; fechaReserva: string; horaReserva: string;
    numPersonas: number; zonaId?: number; recursoId?: number; duracionMinutos?: number;
    tipoEvento?: string; requerimientosEspeciales?: string; notasInternas?: string;
  }) =>
    api<{ id: number; estado: string; codCliente: number }>(
      `/v1/reservas-admin/reserva`, { method: "POST", body: dto },
    ),

  // Vincula una reserva a un cliente real del CRM (evita duplicados).
  vincularCliente: (
    reservaId: number,
    dto: { codCliente: number; actualizarDatos?: boolean },
  ) =>
    api<{
      ok: boolean; codCliente: number; nombreCliente: string; datosActualizados: boolean;
    }>(`/v1/reservas-admin/reserva/${reservaId}/vincular-cliente`, {
      method: "POST", body: dto,
    }),
};

export const reportesApi = {
  reservas: (periodo: "dia" | "semana" | "mes", ref?: string, sucursal?: number, desde?: string, hasta?: string) => {
    const q = new URLSearchParams({ periodo });
    if (ref) q.set("ref", ref);
    if (sucursal) q.set("sucursal", String(sucursal));
    if (desde) q.set("desde", desde);
    if (hasta) q.set("hasta", hasta);
    return api<{
      periodo: string; desde: string; hasta: string;
      totales: { reservas: number; comensales: number; promedioComensales: number };
      tasas: { noShowPct: number; cancelacionPct: number };
      porEstado: Array<{ estado: string; cantidad: number; comensales: number }>;
      porSucursal: Array<{ sucursal: string; cantidad: number; comensales: number }>;
      porOrigen: Array<{ origen: string; cantidad: number }>;
      clientela: { nuevos: number; frecuentes: number };
      porHora: Array<{ hora: number; cantidad: number; comensales: number }>;
    }>(`/v1/reportes/reservas?${q.toString()}`);
  },
  calendario: (anio: number, mes: number, sucursal?: number, todas?: boolean) => {
    const q = new URLSearchParams({ anio: String(anio), mes: String(mes) });
    if (sucursal) q.set("sucursal", String(sucursal));
    if (todas) q.set("todas", "true");
    return api<{
      anio: number; mes: number; desde: string; hasta: string;
      dias: Array<{ fecha: string; reservas: number; comensales: number }>;
    }>(`/v1/reportes/calendario?${q.toString()}`);
  },
};

export interface UsuarioCRM {
  id: string;
  email: string;
  fullName: string;
  telefono?: string;
  role: string;
  isActive: boolean;
  superAdmin: boolean;
  puedeGestionarPermisos: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface PermisoOverrideCRM {
  id: string;
  usuarioId: string;
  ruta: string;
  otorgado: boolean;
  creadoPor?: string;
  createdAt: string;
}

export const usersApi = {
  list: () => api<UsuarioCRM[]>(`/v1/users`),

  create: (dto: {
    email: string;
    fullName: string;
    password: string;
    role: string;
    telefono?: string;
  }) => api<UsuarioCRM>(`/v1/users`, { method: "POST", body: dto }),

  setActivo: (id: string, activo: boolean) =>
    api<UsuarioCRM>(`/v1/users/${id}/activo`, { method: "PATCH", body: { activo } }),

  update: (id: string, dto: { fullName?: string; role?: string; telefono?: string }) =>
    api<UsuarioCRM>(`/v1/users/${id}`, { method: "PATCH", body: dto }),

  resetPassword: (id: string, password: string) =>
    api<{ ok: boolean }>(`/v1/users/${id}/password`, { method: "PATCH", body: { password } }),

  remove: (id: string) =>
    api<{ ok: boolean }>(`/v1/users/${id}`, { method: "DELETE" }),

  listPermisos: (id: string) =>
    api<PermisoOverrideCRM[]>(`/v1/users/${id}/permisos`),

  setPermiso: (id: string, ruta: string, otorgado: boolean) =>
    api<PermisoOverrideCRM>(`/v1/users/${id}/permisos`, { method: "PUT", body: { ruta, otorgado } }),

  removePermiso: (id: string, ruta: string) =>
    api<{ ok: boolean }>(`/v1/users/${id}/permisos/${encodeURIComponent(ruta)}`, { method: "DELETE" }),

  setGestionarPermisos: (id: string, valor: boolean) =>
    api<UsuarioCRM>(`/v1/users/${id}/puede-gestionar-permisos`, { method: "PATCH", body: { valor } }),
};

export const syncApi = {
  resumen: () => api<any[]>(`/v1/sync/resumen`),
  logDetalle: (limit = 50) => api<any[]>(`/v1/sync/log-detalle?limit=${limit}`),
};

// =============================================================
// Cliente 360 - datos reales de ICG (crm_ventas_cab, materialized views)
// =============================================================
export interface Cliente360Item {
  codCliente: number;
  nombre: string | null;
  email: string | null;
  telefono1: string | null;
  mobil: string | null;
  totalVisitas: number;
  totalGastado: string; // numeric -> string
  ticketPromedio: string;
  ultimaVisita: string | null;
  primeraVisita: string | null;
  comensalesPromedio: string | null;
}

export interface Cliente360ListResponse {
  items: Cliente360Item[];
  total: number;
  limit: number;
  offset: number;
}

export interface Cliente360Stats {
  clientesConCompras: number;
  ventasTotal: number;
  lineasTotal: number;
  articulosVendidos: number;
  ventasNeto: string;
  fechaMinima: string;
  fechaMaxima: string;
}

export interface Cliente360Linea {
  numLin: number;
  codArticulo: number;
  descripcion: string;
  cantidad: number;
  precio: number;
  dto: number;
  total: number;
  tipoDelivery: number;
}

export interface Cliente360Venta {
  id: string;
  fecha: string;
  hora: string | null;
  numSerie: string;
  numAlbaran: number;
  sala: number;
  mesa: number;
  sucursalId?: number | null;
  numComensales: number;
  codVendedor: number;
  totalNeto: number;
  totalBruto: number;
  facturado: boolean;
  lineas: Cliente360Linea[];
}

export interface Cliente360TopArticulo {
  codArticulo: number;
  nombreArticulo: string;
  vecesPedido: number;
  totalUnidades: string;
  totalGastado: string;
}

export interface Cliente360VentaCruzada {
  codArticulo: number;
  nombreArticulo: string;
  vecesPedido: number;
  compraJuntoCon: Array<{
    codArticulo: number;
    nombreArticulo: string;
    vecesJunto: number;
    totalGastado: string;
  }>;
}

export interface Cliente360Detalle {
  basico: {
    codCliente: number;
    nombre: string | null;
    cif?: string | null;
    email?: string | null;
    telefono1?: string | null;
    mobil?: string | null;
    direccion?: string | null;
    poblacion?: string | null;
    provincia?: string | null;
    codpostal?: string | null;
    codpais?: string | null;
    fechaNacimiento?: string | null;
    sexo?: string | null;
    bloqueado?: boolean;
  };
  cumpleanos: {
    proximoEnDias: number | null;
    esHoy: boolean;
    mesDia: string | null;
  } | null;
  resumen: {
    totalVisitas: number;
    totalGastado: string;
    ticketPromedio: string;
    ultimaVisita: string;
    primeraVisita: string;
    comensalesPromedio: string | null;
  } | null;
  historial: Cliente360Venta[];
  topProductos: Cliente360TopArticulo[];
  porHora: Array<{ hora: number; visitas: number }>;
  porDiaSemana: Array<{ dia: number; visitas: number }>;
  porMes: Array<{ mes: number; visitas: number }>;
  porComensales: Array<{ numComensales: number; visitas: number }>;
  porZona: Array<{ salaIcg: number; salaNombre: string | null; visitas: number }>;
  porSucursal: Array<{ sucursalId: number | null; sucursalNombre: string; visitas: number; totalGastado: string; ultimaVisita: string | null }>;
  ventasCruzadas: Cliente360VentaCruzada[];
}

export interface ArticuloConfig {
  articuloId: number;
  codArticulo: number;
  nombre: string;
  seccionNombre: string | null;
  familiaNombre: string | null;
  precioUsd: string;
  precioVed: string;
  tieneFoto: boolean;
  fotoIcgUrl: string | null;
  descatalogado: boolean;
  activo: boolean;
  visibleMenu: boolean;
  disponibleDelivery: boolean;
  categoriaId: number | null;
  categoriaDelivery: string | null;
  descripcionDelivery: string | null;
  fotoUrl: string | null;
  precioDeliveryUsd: string | null;
  destacado: boolean;
  agotado: boolean;
  tiempoPreparacionMin: number | null;
}

// ─── Upload de foto de artículo ─────────────────────────────
export async function uploadArticuloFoto(file: File, organizacionId: number, articuloId: number): Promise<{ url: string; filename: string }> {
  const { useAuth } = await import("./auth");
  const token = useAuth.getState().token;
  const BASE = (import.meta as ImportMeta & { env: { VITE_API_URL?: string } }).env.VITE_API_URL ?? "/api";
  const fd = new FormData();
  fd.append("file", file);
  fd.append("organizacionId", String(organizacionId));
  fd.append("articuloId", String(articuloId));
  const r = await fetch(`${BASE}/v1/upload/articulo`, {
    method: "POST", body: fd,
    headers: { Authorization: `Bearer ${token}` },
  });
  if (!r.ok) {
    const t = await r.text();
    throw new Error(t || `HTTP ${r.status}`);
  }
  return r.json();
}

// ─── Pasarelas de pago ───────────────────────────────────────
export type TipoPasarela =
  | "transferencia" | "pago_movil" | "zelle"
  | "paypal" | "stripe" | "mercadopago"
  | "efectivo" | "enlace_pago" | "api_custom";

export interface PasarelaPago {
  id: string;
  organizacionId: number;
  nombre: string;
  tipo: TipoPasarela;
  modo: "sandbox" | "live";
  activa: boolean;
  orden: number;
  config: Record<string, any>;
  instrucciones?: string;
  iconoUrl?: string;
  confirmacionAutomatica: boolean;
  moneda: string;
  comisionFija: string;
  comisionPct: string;
}

// ─── Pedidos delivery ──────────────────────────────────────
export type EstadoPedido =
  | "nuevo" | "confirmado" | "en_preparacion" | "listo"
  | "en_camino" | "entregado" | "cancelado";

export type EstadoPagoPedido = "pendiente" | "reportado" | "confirmado" | "rechazado";
export type TipoEntrega = "delivery" | "retiro" | "mesa";
export type CanalOrigen = "admin" | "web" | "whatsapp" | "telefono";

export interface PedidoLin {
  id: string;
  numLin: number;
  articuloId?: number;
  codArticulo?: number;
  nombreArticulo: string;
  fotoUrl?: string;
  cantidad: string;
  precioUnitario: string;
  descuento: string;
  subtotal: string;
  observaciones?: string;
}

export interface PedidoLog {
  id: string;
  estadoAnterior?: string;
  estadoNuevo: string;
  actorEmail?: string;
  nota?: string;
  createdAt: string;
}

export interface Pedido {
  id: string;
  numeroPedido: string;
  codCliente?: number;
  nombreCliente: string;
  telefono?: string;
  email?: string;
  tipoEntrega: TipoEntrega;
  deliveryZonaId?: number;
  nombreZona?: string;
  direccionEntrega?: string;
  referenciaDireccion?: string;
  pasarelaId?: string;
  estadoPago: EstadoPagoPedido;
  subtotal: string;
  costoEnvio: string;
  descuento: string;
  total: string;
  moneda: string;
  estado: EstadoPedido;
  horaSolicitud: string;
  horaEstimadaEntrega?: string;
  horaRealEntrega?: string;
  notasCliente?: string;
  notasInternas?: string;
  motivoCancelacion?: string;
  canalOrigen: CanalOrigen;
  lineas: PedidoLin[];
  logs?: PedidoLog[];
}

export interface PedidoStats {
  pedidosHoy: number;
  activos: number;
  entregadosHoy: number;
  canceladosHoy: number;
  ventasHoy: string;
  porConfirmar: number;
  enPreparacion: number;
  listos: number;
  enCamino: number;
}

export const pedidosApi = {
  list: (params: {
    estado?: EstadoPedido | EstadoPedido[];
    estadoPago?: EstadoPagoPedido;
    tipoEntrega?: TipoEntrega;
    canalOrigen?: CanalOrigen;
    desde?: string;
    hasta?: string;
    busqueda?: string;
    limit?: number;
    offset?: number;
  } = {}) => {
    const q = new URLSearchParams();
    if (params.estado) q.set("estado", Array.isArray(params.estado) ? params.estado.join(",") : params.estado);
    if (params.estadoPago) q.set("estadoPago", params.estadoPago);
    if (params.tipoEntrega) q.set("tipoEntrega", params.tipoEntrega);
    if (params.canalOrigen) q.set("canalOrigen", params.canalOrigen);
    if (params.desde) q.set("desde", params.desde);
    if (params.hasta) q.set("hasta", params.hasta);
    if (params.busqueda) q.set("busqueda", params.busqueda);
    if (params.limit) q.set("limit", String(params.limit));
    if (params.offset) q.set("offset", String(params.offset));
    return api<{ items: Pedido[]; total: number; limit: number; offset: number }>(
      `/v1/pedidos?${q.toString()}`,
    );
  },

  stats: () => api<PedidoStats>(`/v1/pedidos/stats`),

  get: (id: number) => api<Pedido>(`/v1/pedidos/${id}`),

  crear: (dto: any) => api<Pedido>(`/v1/pedidos`, { method: "POST", body: dto }),

  cambiarEstado: (id: number, dto: { estado: EstadoPedido; nota?: string; motivoCancelacion?: string }) =>
    api<Pedido>(`/v1/pedidos/${id}/estado`, { method: "PATCH", body: dto }),

  actualizarPago: (id: number, dto: { estadoPago: EstadoPagoPedido; nota?: string }) =>
    api<Pedido>(`/v1/pedidos/${id}/pago`, { method: "PATCH", body: dto }),

  actualizarNotas: (id: number, dto: { notasInternas?: string; notasCliente?: string; horaEstimadaEntrega?: string }) =>
    api<Pedido>(`/v1/pedidos/${id}/notas`, { method: "PATCH", body: dto }),
};

// ── Delivery Zonas ──
export interface DeliveryZona {
  id: number;
  organizacionId: number;
  sucursalId?: number;
  nombre: string;
  costoEnvio: string;
  tiempoEstimadoMin?: number;
  descripcion?: string;
  activa: boolean;
  orden: number;
}

export const deliveryZonasApi = {
  list: (activas?: boolean) =>
    api<DeliveryZona[]>(`/v1/delivery-zonas${activas ? "?activas=true" : ""}`),
  get: (id: number) => api<DeliveryZona>(`/v1/delivery-zonas/${id}`),
  crear: (dto: Partial<DeliveryZona>) =>
    api<DeliveryZona>(`/v1/delivery-zonas`, { method: "POST", body: dto }),
  actualizar: (id: number, dto: Partial<DeliveryZona>) =>
    api<DeliveryZona>(`/v1/delivery-zonas/${id}`, { method: "PATCH", body: dto }),
  eliminar: (id: number) =>
    api<{ ok: boolean }>(`/v1/delivery-zonas/${id}`, { method: "DELETE" }),
};

export const pasarelasApi = {
  list: () => api<PasarelaPago[]>(`/v1/pasarelas`),
  get: (id: number) => api<PasarelaPago>(`/v1/pasarelas/${id}`),
  crear: (dto: Partial<PasarelaPago>) =>
    api<PasarelaPago>(`/v1/pasarelas`, { method: "POST", body: dto }),
  actualizar: (id: number, dto: Partial<PasarelaPago>) =>
    api<PasarelaPago>(`/v1/pasarelas/${id}`, { method: "PATCH", body: dto }),
  eliminar: (id: number) =>
    api<{ ok: boolean }>(`/v1/pasarelas/${id}`, { method: "DELETE" }),
  listarPagos: (params: { estado?: string; limit?: number; offset?: number } = {}) => {
    const q = new URLSearchParams();
    Object.entries(params).forEach(([k, v]) => { if (v != null) q.set(k, String(v)); });
    return api<{ items: any[]; total: number }>(`/v1/pasarelas/pagos/lista?${q.toString()}`);
  },
};

export const articulosConfigApi = {
  list: (params: {
    busqueda?: string;
    seccion?: string;
    soloDelivery?: boolean;
    soloMenu?: boolean;
    limit?: number;
    offset?: number;
  } = {}) => {
    const q = new URLSearchParams();
    if (params.busqueda) q.set("busqueda", params.busqueda);
    if (params.seccion) q.set("seccion", params.seccion);
    if (params.soloDelivery) q.set("soloDelivery", "true");
    if (params.soloMenu) q.set("soloMenu", "true");
    if (params.limit) q.set("limit", String(params.limit));
    if (params.offset) q.set("offset", String(params.offset));
    return api<{ items: ArticuloConfig[]; total: number; limit: number; offset: number }>(
      `/v1/articulos-config?${q.toString()}`,
    );
  },

  secciones: () =>
    api<Array<{ seccionNombre: string; total: number }>>(`/v1/articulos-config/secciones`),

  actualizar: (articuloId: number, dto: Partial<{
    visibleMenu: boolean;
    disponibleDelivery: boolean;
    categoriaId: number | null;
    categoriaDelivery: string | null;
    destacado: boolean;
    agotado: boolean;
    precioDeliveryUsd: number | null;
    descripcionDelivery: string | null;
    tiempoPreparacionMin: number | null;
    fotoUrl: string | null;
  }>) =>
    api(`/v1/articulos-config/${articuloId}`, { method: "PATCH", body: dto }),

  bulkMarkupDelivery: (porcentaje: number, opts?: { soloVisiblesDelivery?: boolean; reset?: boolean }) =>
    api<{ ok: boolean; actualizados: number; porcentaje?: number; modo: string; }>(
      `/v1/articulos-config/bulk-markup-delivery`,
      { method: "POST", body: { porcentaje, ...opts } }
    ),

  bulkToggle: (articuloIds: number[], campo: "visibleMenu" | "disponibleDelivery", valor: boolean) =>
    api(`/v1/articulos-config/bulk-toggle`, {
      method: "POST", body: { articuloIds, campo, valor },
    }),
};

export const cliente360Api = {
  list: (params: {
    orden?: "ultima_visita" | "total_gastado" | "total_visitas";
    limit?: number;
    offset?: number;
    busqueda?: string;
    sucursalId?: number;
  } = {}) => {
    const q = new URLSearchParams();
    if (params.orden) q.set("orden", params.orden);
    if (params.limit) q.set("limit", String(params.limit));
    if (params.offset) q.set("offset", String(params.offset));
    if (params.busqueda) q.set("busqueda", params.busqueda);
    if (params.sucursalId) q.set("sucursalId", String(params.sucursalId));
    return api<Cliente360ListResponse>(`/v1/cliente360?${q.toString()}`);
  },

  stats: (sucursalId?: number) =>
    api<Cliente360Stats>(`/v1/cliente360/stats${sucursalId ? `?sucursalId=${sucursalId}` : ""}`),

  exportarCsv: (tipo: "vip" | "enRiesgo") => {
    const hoy = new Date().toISOString().slice(0, 10);
    return apiDownload(`/v1/cliente360/segmento/${tipo}/csv`, `clientes_${tipo}_${hoy}.csv`);
  },

  campanaMasiva: (body: {
    canal: "email" | "whatsapp";
    segmento?: "vip" | "enRiesgo";
    codClientes?: number[];
    asunto?: string;
    cuerpo: string;
    esHtml?: boolean;
  }) => api<{
    ok: boolean;
    canal: "email" | "whatsapp";
    totalCandidatos: number;
    sinCanal: number;
    enviados: number;
    fallidos: number;
    erroresMuestra: Array<{ codCliente: number; nombre: string; motivo: string }>;
  }>(`/v1/cliente360/campana-masiva`, { method: "POST", body }),

  kpisAvanzados: (sucursalId?: number) => api<{
    ticketPorTamano: Array<{ rango: string; visitas: number; totalComensales: number;
      ticketPromedio: string; ticketPorComensal: string; ventasTotales: string; }>;
    topFamilias: Array<{ familia: string; ticketsConProducto: number; unidadesVendidas: number;
      ventasUsd: string; promedioLineaUsd: string; }>;
    retencion: { clientesUnicos: number; soloUnaVez: number; recurrentes: number;
      fidelizados: number; visitasPromedio: string; tasaRetornoPct: string; } | null;
    habitosGenerales: {
      conteos: { individuales: number; grupales: number; total: number };
      individual: {
        imprescindibles: Array<{ codArticulo: number; nombre: string; familia: string;
          tickets: number; pctTickets: number; }>;
        composicion: Array<{ familia: string; gastoUsd: number; unidades: number;
          pctGasto: number; pctUnidades: number; }>;
        combos: Array<{ codX: number; nombreX: string; familiaX: string; vecesX: number;
          codY: number; nombreY: string; familiaY: string;
          vecesJuntos: number; confianzaPct: number; }>;
      };
      grupo: {
        imprescindibles: Array<{ codArticulo: number; nombre: string; familia: string;
          tickets: number; pctTickets: number; }>;
        composicion: Array<{ familia: string; gastoUsd: number; unidades: number;
          pctGasto: number; pctUnidades: number; }>;
        combos: Array<{ codX: number; nombreX: string; familiaX: string; vecesX: number;
          codY: number; nombreY: string; familiaY: string;
          vecesJuntos: number; confianzaPct: number; }>;
      };
    };
    distribucionEdad: {
      conteos: {
        conFechaReal: number; conCedulaEstimada: number;
        confianzaBaja: number; sinDeterminar: number;
        totalAnalizables: number;
      };
      rangos: Array<{
        rango: "<20" | "20-30" | "30-40" | "40-60" | "60+";
        clientes: number; visitas: number; gastoUsd: number;
        masculinos: number; femeninos: number; ticketPromedio: number;
      }>;
    };
    sexoStats: {
      resumen: Array<{ sexo: string; clientesUnicos: number; visitasTotal: number;
        visitasPromedio: number; ticketPromedioUsd: number; gastoTotalUsd: number;
        gastoPromedioPorCliente: number; recurrentes: number; tasaRetornoPct: number; }>;
      composicionM: Array<{ familia: string; gastoUsd: number; unidades: number; pctGasto: number; }>;
      composicionF: Array<{ familia: string; gastoUsd: number; unidades: number; pctGasto: number; }>;
    };
    segmentoVip: Array<{ codCliente: number; nombre: string; email?: string;
      telefono?: string; sexo?: string; visitas: number;
      ticketPromedio: string; gastoTotal: string; }>;
    enRiesgo: Array<{ codCliente: number; nombre: string; email?: string;
      telefono?: string; sexo?: string; visitas: number;
      ticketPromedio: string; gastoTotal: string;
      ultimaVisita: string; diasSinVolver: number; }>;
  }>(`/v1/cliente360/kpis-avanzados${sucursalId ? `?sucursalId=${sucursalId}` : ""}`),

  dashboard: (params: { desde?: string; hasta?: string; sucursalId?: number } = {}) => {
    const q = new URLSearchParams();
    if (params.desde) q.set("desde", params.desde);
    if (params.hasta) q.set("hasta", params.hasta);
    if (params.sucursalId) q.set("sucursalId", String(params.sucursalId));
    const qs = q.toString();
    return api<{
      kpis: {
        totalVisitas: number;
        clientesUnicos: number;
        ingresoTotal: string;
        ticketPromedio: string;
        comensalesPromedio: string;
      } | null;
      tendenciaMensual: Array<{ mes: string; visitas: number; clientes: number; ingreso: string }>;
      porHora: Array<{ hora: number; visitas: number }>;
      porDiaSemana: Array<{ dia: number; visitas: number }>;
      porMes: Array<{ mes: number; visitas: number }>;
      porComensales: Array<{ numComensales: number; visitas: number }>;
      porZona: Array<{ salaIcg: number; salaNombre: string | null; visitas: number }>;
      topProductos: Array<{
        codArticulo: number; nombreArticulo: string; vecesPedido: number;
        totalUnidades: string; totalGastado: string;
      }>;
    }>(`/v1/cliente360/dashboard${qs ? `?${qs}` : ""}`);
  },

  actualizarDatos: (codCliente: number, dto: {
    nombreCliente?: string;
    cif?: string | null;
    email?: string | null;
    telefono1?: string | null;
    mobil?: string | null;
    direccion1?: string | null;
    poblacion?: string | null;
    fechaNacimiento?: string | null;
    sexo?: "M" | "F" | null;
    instagram?: string | null;
    twitter?: string | null;
    facebook?: string | null;
    tiktok?: string | null;
  }) =>
    api<{ ok: boolean; codCliente: number }>(`/v1/cliente360/${codCliente}/datos`, {
      method: "PATCH", body: dto,
    }),

  sugerirSexo: (codCliente: number) =>
    api<{
      codCliente: number;
      nombreCliente: string;
      sexoActual: string | null;
      sugerencia: "M" | "F" | null;
      confianza: "alta" | "media" | "baja";
      razon: string;
    }>(`/v1/cliente360/${codCliente}/sugerir-sexo`),

  aplicarSexoBulk: (soloConfianzaAlta = true) =>
    api<{
      ok: boolean;
      totalSinSexo: number;
      actualizados: number;
      omitidos: number;
      detalles: { alta: number; media: number; baja: number; sinDeterminar: number };
    }>(`/v1/cliente360/aplicar-sexo-bulk`, {
      method: "POST", body: { soloConfianzaAlta },
    }),

  detalle: (codCliente: number, opts: { limitVentas?: number; limitTopArticulos?: number } = {}) => {
    const q = new URLSearchParams();
    if (opts.limitVentas) q.set("limitVentas", String(opts.limitVentas));
    if (opts.limitTopArticulos) q.set("limitTopArticulos", String(opts.limitTopArticulos));
    const qs = q.toString();
    return api<Cliente360Detalle>(`/v1/cliente360/${codCliente}${qs ? `?${qs}` : ""}`);
  },
};

export const eventosApi = {
  /** Lista de eventos (filtro por sucursal y fechas) */
  list: (params?: { sucursalId?: number; desde?: string; hasta?: string }) => {
    const q = new URLSearchParams();
    if (params?.sucursalId) q.set("sucursalId", String(params.sucursalId));
    if (params?.desde)      q.set("desde", params.desde);
    if (params?.hasta)      q.set("hasta", params.hasta);
    return api<any[]>(`/v1/eventos?${q}`);
  },
  get:       (id: number)       => api<any>(`/v1/eventos/${id}`),
  create:    (body: any)        => api<any>(`/v1/eventos`, { method: "POST", body }),
  update:    (id: number, body: any) => api<any>(`/v1/eventos/${id}`, { method: "PUT", body }),
  checkIn:   (invId: number, detalle?: { observacionLlegada?: string; acompanantesReales?: number | null }) =>
    api(`/v1/eventos/invitados/${invId}/checkin`, { method: "POST", body: detalle ?? {} }),
  undoCheckIn: (invId: number)  => api(`/v1/eventos/invitados/${invId}/undo-checkin`, { method: "POST" }),
  noShow:    (invId: number)    => api(`/v1/eventos/invitados/${invId}/no-show`, { method: "POST" }),
  removeInvitado: (invId: number) => api(`/v1/eventos/invitados/${invId}`, { method: "DELETE" }),
  addBulk:   (id: number, invitados: any[]) => api(`/v1/eventos/${id}/invitados/bulk`, { method: "POST", body: { invitados } }),
  updateChecklistItem: (id: number, patch: { itemId: string; estado?: string; responsable?: string; usuarioId?: string | null; fechaEntrega?: string; completadoPor?: string; observacion?: string }) =>
    api(`/v1/eventos/${id}/checklist/item`, { method: "POST", body: patch }),
  saveChecklist: (id: number, checklist: any[]) =>
    api(`/v1/eventos/${id}/checklist`, { method: "PUT", body: { checklist } }),
  getConfig: () => api<{ tagsObservaciones: string[]; checklistTemplate: string[] }>(`/v1/eventos/config/get`),
  saveConfig: (dto: { tagsObservaciones?: string[]; checklistTemplate?: string[] }) =>
    api(`/v1/eventos/config/save`, { method: "PUT", body: dto }),
  /** Tareas del checklist asignadas al usuario actual */
  misTareas: () => api<any[]>(`/v1/eventos/mis-tareas`),
};

export const whatsappApi = {
  getConfig:  () => api<any>(`/v1/whatsapp/config`),
  saveConfig: (dto: any) => api<any>(`/v1/whatsapp/config`, { method: 'PUT', body: dto }),
  test:       () => api<any>(`/v1/whatsapp/test`, { method: 'POST' }),
  sendTest:   (telefono: string) => api<any>(`/v1/whatsapp/send-test`, { method: 'POST', body: { telefono } }),
  webhookUrl: () => api<any>(`/v1/whatsapp/webhook-url`),
  instanciaEstado:   () => api<any>(`/v1/whatsapp/instancia/estado`),
  instanciaConectar: () => api<any>(`/v1/whatsapp/instancia/conectar`, { method: 'POST' }),
  instanciaLogout:   () => api<any>(`/v1/whatsapp/instancia/logout`, { method: 'POST' }),

  conversaciones: (opts: { busqueda?: string; limit?: number; offset?: number } = {}) => {
    const q = new URLSearchParams();
    if (opts.busqueda) q.set('busqueda', opts.busqueda);
    if (opts.limit) q.set('limit', String(opts.limit));
    if (opts.offset) q.set('offset', String(opts.offset));
    return api<{ items: any[]; total: number }>(`/v1/whatsapp/conversaciones?${q.toString()}`);
  },
  mensajesConversacion: (telefono: string, opts: { limit?: number; before?: string } = {}) => {
    const q = new URLSearchParams();
    if (opts.limit) q.set('limit', String(opts.limit));
    if (opts.before) q.set('before', opts.before);
    return api<any[]>(`/v1/whatsapp/conversaciones/${encodeURIComponent(telefono)}/mensajes?${q.toString()}`);
  },
  pausarConversacion:   (telefono: string) => api<{ ok: boolean }>(`/v1/whatsapp/conversaciones/${encodeURIComponent(telefono)}/pausar`, { method: 'POST' }),
  reanudarConversacion: (telefono: string) => api<{ ok: boolean }>(`/v1/whatsapp/conversaciones/${encodeURIComponent(telefono)}/reanudar`, { method: 'POST' }),
  metricas: (opts: { desde?: string; hasta?: string } = {}) => {
    const q = new URLSearchParams();
    if (opts.desde) q.set('desde', opts.desde);
    if (opts.hasta) q.set('hasta', opts.hasta);
    return api<any>(`/v1/whatsapp/metricas?${q.toString()}`);
  },
};

export const tareasApi = {
  mias:       () => api<any[]>(`/v1/tareas/mias`),
  list:       () => api<any[]>(`/v1/tareas`),
  crear:      (dto: { titulo: string; descripcion?: string; prioridad?: string; destinatarios: {id: string; nombre: string}[]; fechaLimite?: string }) =>
                api<any>(`/v1/tareas`, { method: 'POST', body: dto }),
  actualizar: (id: number, dto: { titulo?: string; descripcion?: string; prioridad?: string; destinatarios?: {id: string; nombre: string}[]; fechaLimite?: string | null }) =>
                api<any>(`/v1/tareas/${id}`, { method: 'PUT', body: dto }),
  completar:  (id: number) => api<any>(`/v1/tareas/${id}/completar`, { method: 'PATCH' }),
  reabrir:    (id: number) => api<any>(`/v1/tareas/${id}/reabrir`, { method: 'PATCH' }),
  eliminar:   (id: number) => api<any>(`/v1/tareas/${id}`, { method: 'DELETE' }),
};

// =============================================================
// Audiencias / Segmentos
// =============================================================
export interface Audiencia {
  id: string; nombre: string; tipo: "dinamica" | "fija";
  criterios: import("../routes/audiencias/criterios").Criterios; creadoEn: string;
}
export interface PreviewAudiencia { total: number; con_opt_in: number; muestra: Record<string, unknown>[] }

const qOrg = (org?: number) => (org ? `?org=${org}` : "");
export const audienciasApi = {
  listar: (org?: number) => api<Audiencia[]>(`/v1/audiencias${qOrg(org)}`),
  crear: (body: unknown, org?: number) => api<Audiencia>(`/v1/audiencias${qOrg(org)}`, { method: "POST", body }),
  preview: (criterios: unknown, org?: number) =>
    api<PreviewAudiencia>(`/v1/audiencias/preview${qOrg(org)}`, { method: "POST", body: { criterios } }),
  obtener: (id: string, org?: number) => api<Audiencia>(`/v1/audiencias/${id}${qOrg(org)}`),
  actualizar: (id: string, body: unknown, org?: number) =>
    api<Audiencia>(`/v1/audiencias/${id}${qOrg(org)}`, { method: "PATCH", body }),
  eliminar: (id: string, org?: number) => api<{ ok: true }>(`/v1/audiencias/${id}${qOrg(org)}`, { method: "DELETE" }),
  miembros: (id: string, page = 1, limit = 50, org?: number) =>
    api<{ total: number; filas: Record<string, unknown>[] }>(
      `/v1/audiencias/${id}/miembros?page=${page}&limit=${limit}${org ? `&org=${org}` : ""}`),
  agregar: (id: string, cod_clientes: number[], org?: number) =>
    api<{ agregados: number }>(`/v1/audiencias/${id}/miembros${qOrg(org)}`, { method: "POST", body: { cod_clientes } }),
  exportar: (id: string, org?: number) =>
    apiDownload(`/v1/audiencias/${id}/export?formato=csv${org ? `&org=${org}` : ""}`, `audiencia-${id}.csv`),
};
// ────────────────────────────────────────────────────────────
// Encuestas
// ────────────────────────────────────────────────────────────
export interface EncuestaOpcionDto { texto: string }
export interface EncuestaPreguntaDto {
  tipo: "texto" | "seleccion" | "rating";
  textoPregunta: string;
  requerida?: boolean;
  opciones?: EncuestaOpcionDto[];
}
export interface EncuestaListItem {
  id: number;
  titulo: string;
  descripcion?: string | null;
  activa: boolean;
  modo: "whatsapp" | "qr";
  tokenQr?: string | null;
  sucursalId?: number | null;
  sucursalNombre?: string | null;
  createdAt: string;
  totalPreguntas: number;
  totalEnviadas: number;
  totalRespondidas: number;
}

export interface EncuestaQrActiva {
  id: number;
  titulo: string;
  tokenQr: string;
  sucursalId?: number | null;
  sucursalNombre?: string | null;
  createdAt: string;
}

export const encuestasApi = {
  listar: () => api<EncuestaListItem[]>(`/v1/encuestas`),
  // Lectura liviana para personal con contacto directo con el cliente
  // (cajero, operativo/mesonero): solo ven el QR, no gestionan encuestas.
  listarActivasQr: () => api<EncuestaQrActiva[]>(`/v1/encuestas/activas/qr`),
  crear: (dto: { titulo: string; descripcion?: string; modo?: "whatsapp" | "qr"; sucursalId?: number; preguntas: EncuestaPreguntaDto[] }) =>
    api<{ id: number; modo: string; tokenQr?: string }>(`/v1/encuestas`, { method: "POST", body: dto }),
  detalle: (id: number) => api<any>(`/v1/encuestas/${id}`),
  eliminar: (id: number) => api<{ ok: boolean }>(`/v1/encuestas/${id}`, { method: "DELETE" }),
  enviarMasivo: (id: number) =>
    api<{ enviados: number; omitidos: number; totalClientes: number }>(
      `/v1/encuestas/${id}/enviar-masivo`, { method: "POST" },
    ),
  // Públicos (sin auth) — usan fetch directo porque api() siempre manda el token si hay sesión,
  // y acá puede no haber ninguna (cliente final sin login).
  obtenerPorToken: (token: string) => api<any>(`/v1/encuestas/responder/${token}`),
  responder: (token: string, respuestas: Array<{ preguntaId: number; opcionId?: number; texto?: string }>) =>
    api<{ ok: boolean; mensaje: string }>(`/v1/encuestas/responder/${token}`, {
      method: "POST", body: { respuestas },
    }),
  misRespuestas: (identificador: string) =>
    api<{ envios: any[] }>(`/v1/encuestas/mis-respuestas`, { method: "POST", body: { identificador } }),
  // Modo QR: link genérico reutilizable, sin cliente pre-asignado.
  obtenerPorTokenQr: (tokenQr: string) => api<any>(`/v1/encuestas/qr/${tokenQr}`),
  lookupClienteQr: (tokenQr: string, params: { telefono?: string; cif?: string }) => {
    const qs = new URLSearchParams();
    if (params.telefono) qs.set("telefono", params.telefono);
    if (params.cif) qs.set("cif", params.cif);
    return api<{
      nombreCliente: string; cif?: string; telefono?: string;
      instagram?: string; twitter?: string; facebook?: string; tiktok?: string;
    } | null>(`/v1/encuestas/qr/${tokenQr}/lookup-cliente?${qs}`);
  },
  responderQr: (tokenQr: string, dto: {
    nombreCliente: string;
    cif?: string;
    telefono?: string;
    instagram?: string;
    twitter?: string;
    facebook?: string;
    tiktok?: string;
    respuestas: Array<{ preguntaId: number; opcionId?: number; texto?: string }>;
  }) =>
    api<{ ok: boolean; mensaje: string }>(`/v1/encuestas/qr/${tokenQr}`, { method: "POST", body: dto }),
};

export interface WalkinEntrada {
  id: number;
  nombre: string;
  telefono: string;
  personas: number;
  estado: "esperando" | "llamado" | "sentado" | "no_show" | "cancelado";
  posicion: number | null;
  createdAt?: string;
  llamadoAt?: string | null;
}

export const walkinApi = {
  // ── Público (QR de la puerta, sin login) ──
  crear: (dto: { sucursalId: number; nombre: string; telefono: string; personas?: number }) =>
    api<{ id: number; posicion: number }>("/v1/walkin", { method: "POST", body: dto }),
  status: (id: number) =>
    api<{ id: number; nombre: string; personas: number; estado: WalkinEntrada["estado"]; posicion: number | null }>(
      `/v1/walkin/${id}/status`,
    ),

  // ── Personal (recepción/host) ──
  listar: (sucursalId: number) =>
    api<WalkinEntrada[]>(`/v1/walkin?sucursalId=${sucursalId}`),
  llamar: (id: number) => api<WalkinEntrada>(`/v1/walkin/${id}/llamar`, { method: "POST" }),
  volverEsperando: (id: number) => api<WalkinEntrada>(`/v1/walkin/${id}/volver-esperando`, { method: "POST" }),
  sentar: (id: number) => api<WalkinEntrada>(`/v1/walkin/${id}/sentar`, { method: "POST" }),
  noShow: (id: number) => api<WalkinEntrada>(`/v1/walkin/${id}/no-show`, { method: "POST" }),
  cancelar: (id: number) => api<WalkinEntrada>(`/v1/walkin/${id}/cancelar`, { method: "POST" }),

  /** Para la campanita y el badge del dashboard. */
  notificaciones: () => api<WalkinNotificacion[]>("/v1/walkin/notificaciones"),
};

export interface WalkinNotificacion {
  id: number;
  nombre: string;
  personas: number;
  sucursalId: number;
  sucursalNombre: string;
  createdAt: string;
}

// =============================================================
// Eventos públicos (venta de entradas con pago anticipado)
// =============================================================

export type EstadoEventoPublico = "borrador" | "publicado" | "cerrado" | "cancelado";
export type EstadoCompraEvento = "pendiente_pago" | "confirmada" | "rechazada" | "expirada" | "cancelada";

export interface EventoPublicoAdmin {
  id: number;
  organizacion_id: number;
  sucursal_id: number | null;
  titulo: string;
  descripcion?: string | null;
  imagen_url?: string | null;
  red_social_url?: string | null;
  fecha_evento: string;
  hora_inicio?: string | null;
  hora_fin?: string | null;
  precio_usd: string;
  capacidad_max: number;
  horas_limite_pago: number;
  instrucciones_pago?: string | null;
  estado: EstadoEventoPublico;
  entradasVendidas?: number;
  totalCobradoUsd?: string;
}

export interface EventoPublicoCatalogo {
  id: number;
  titulo: string;
  descripcion?: string | null;
  imagenUrl?: string | null;
  redSocialUrl?: string | null;
  fechaEvento: string;
  horaInicio?: string | null;
  horaFin?: string | null;
  precioUsd: string;
  capacidadMax: number;
  cuposDisponibles: number;
}

export interface SucursalBranding {
  sucursalId?: number | null;
  sucursalNombre?: string | null;
  logoUrl?: string | null;
  colorPrimario?: string | null;
  imagenFondoUrl?: string | null;
}

export interface EventoPublicoDetalle extends EventoPublicoCatalogo, SucursalBranding {
  estado: EstadoEventoPublico;
  instruccionesPago?: string | null;
  horasLimitePago: number;
}

export interface CompraEventoStatus {
  id: number;
  estado: EstadoCompraEvento;
  nombre: string;
  cantidadEntradas: number;
  montoTotalUsd: number;
  checkInToken: string | null;
  checkInAt?: string | null;
  expiraEn: string | null;
  minutosRestantes: number | null;
  evento: ({
    id: number;
    titulo: string;
    fechaEvento: string;
    horaInicio?: string | null;
    imagenUrl?: string | null;
    instruccionesPago?: string | null;
  } & SucursalBranding) | null;
  ultimoPago: {
    estado: string;
    montoUsd: number;
    motivoRechazo?: string | null;
    createdAt: string;
  } | null;
}

export interface PagoPendienteEvento {
  id: number;
  compraId: number;
  nombrePagador: string;
  telefono?: string | null;
  email?: string | null;
  montoUsd: string;
  moneda: string;
  metodo?: string | null;
  banco?: string | null;
  numeroReferencia?: string | null;
  fechaPago?: string | null;
  comprobanteUrl?: string | null;
  notasCliente?: string | null;
  createdAt: string;
  cantidadEntradas: number;
  montoTotalUsd: string;
  eventoTitulo: string;
  fechaEvento: string;
}

export const eventosPublicosApi = {
  // ── Público ──
  catalogo: (sucursalId?: number) =>
    api<EventoPublicoCatalogo[]>(`/v1/eventos-publicos${sucursalId ? `?sucursalId=${sucursalId}` : ""}`),
  detalle: (id: number) => api<EventoPublicoDetalle>(`/v1/eventos-publicos/${id}`),
  comprar: (id: number, dto: { nombre: string; telefono: string; email?: string; cedula?: string; cantidadEntradas?: number }) =>
    api<{ compraId: number; montoTotalUsd: number; horasLimitePago: number; instruccionesPago?: string | null }>(
      `/v1/eventos-publicos/${id}/comprar`, { method: "POST", body: dto },
    ),
  reportarPago: (compraId: number, dto: unknown) =>
    api(`/v1/eventos-publicos/compras/${compraId}/reportar-pago`, { method: "POST", body: dto }),
  status: (compraId: number) => api<CompraEventoStatus>(`/v1/eventos-publicos/compras/${compraId}/status`),
  checkIn: (token: string) =>
    api<{ id: number; nombre: string; cantidadEntradas: number; estado: string; yaUsada: boolean; checkInAt?: string | null; eventoTitulo?: string; eventoFecha?: string | null } & SucursalBranding>(
      `/v1/eventos-publicos/checkin/${token}`, { method: "POST" },
    ),

  // ── Admin ──
  listarAdmin: () => api<EventoPublicoAdmin[]>(`/v1/admin/eventos-publicos`),
  obtenerAdmin: (id: number) => api<EventoPublicoAdmin>(`/v1/admin/eventos-publicos/${id}`),
  crear: (dto: unknown) => api<EventoPublicoAdmin>(`/v1/admin/eventos-publicos`, { method: "POST", body: dto }),
  editar: (id: number, dto: unknown) => api<EventoPublicoAdmin>(`/v1/admin/eventos-publicos/${id}`, { method: "PUT", body: dto }),
  eliminar: (id: number) => api<{ ok: boolean }>(`/v1/admin/eventos-publicos/${id}`, { method: "DELETE" }),
  publicar: (id: number) => api(`/v1/admin/eventos-publicos/${id}/publicar`, { method: "POST" }),
  cerrar: (id: number) => api(`/v1/admin/eventos-publicos/${id}/cerrar`, { method: "POST" }),
  cancelar: (id: number) => api(`/v1/admin/eventos-publicos/${id}/cancelar`, { method: "POST" }),
  anunciarWhatsapp: (id: number) => api<{ enviados: number }>(`/v1/admin/eventos-publicos/${id}/anunciar-whatsapp`, { method: "POST" }),
  resumen: () => api<{ eventosPublicados: number; entradasConfirmadas: number; totalCobradoUsd: string; pagosPendientes: number }>(`/v1/admin/eventos-publicos/resumen`),
  pagosPendientes: () => api<PagoPendienteEvento[]>(`/v1/admin/eventos-publicos/pagos-pendientes`),
  aprobarPago: (id: number) => api(`/v1/admin/eventos-publicos/pagos/${id}/aprobar`, { method: "POST" }),
  rechazarPago: (id: number, motivo: string) => api(`/v1/admin/eventos-publicos/pagos/${id}/rechazar`, { method: "POST", body: { motivo } }),
};
